// The distortion knob's "baked" mode (js/quality.js distortionMode): the
// glass's refraction wobble read from a 3D texture instead of computed per
// pixel per frame.
//
// What the live wobble computes (MeshTransmissionMaterial, @pmndrs/vanilla
// 1.25.0), once per glass pixel before its refraction samples:
//   distortionNormal = distortion * vec3(snoiseFractal(pos * distortionScale),
//                                        snoiseFractal(pos.zxy * distortionScale),
//                                        snoiseFractal(pos.yxz * distortionScale))
// (with temporalDistortion 0, as here), added to the normal each sample
// refracts along. `pos` is the fragment's WORLD position, and
// snoiseFractal is four octaves of 3D simplex noise: twelve noise
// evaluations per pixel, in each of the two glass passes.
//
// The bake evaluates the same three fractals, with the same GLSL (copied
// below; withBakedDistortion checks the copy still matches the material's),
// on a grid over the glass's bounding box in the cross's own space (the space
// of the `group` that every pose moves), once, on the GPU: one draw per layer
// of a 3D render target. The glass then samples it (trilinear) at the
// fragment's position in that same space: one texture lookup per pixel.
//
// How baked differs from live (measured on an M1 Max, ANGLE/Metal):
//   - not the same pattern. The noise's gradient hash, random3, is
//     fract(512 * 4096 * sin(dot(lattice point, ...))): it multiplies the last
//     bit of the sine by about two million, so each shader program the GPU
//     compiler builds rounds its way to its own set of gradients. The bake's
//     program and another small program agree (to 4e-5 at the texel
//     centers), but the material's own program hashes the same lattice point
//     to an unrelated value. So baked is the same noise function — the same
//     four octaves, cell sizes and strength (the offset's RMS about 3.4e-3
//     baked, 3.1e-3 live) — but a different draw of it.
//   - fixed to the cross. Live noise is fixed in the world, so as the cross
//     turns the wobble slides through it; baked turns with the cross, like
//     the print's actual surface.
//   - a finite grid: TEXELS_PER_UNIT puts 3 texels across the finest octave's
//     noise cells (1/16 world unit at distortionScale 2); trilinear
//     filtering between them is within 0.007 of the noise on average (of
//     values around 0.14), and softens that octave slightly.
// The field is 112 x 162 x 26 half-float RGBA texels: 3.8 MB of GPU memory.
import * as THREE from "three";

// grid density, texels per world unit (the cross is 3.3 units tall).
// Measured mean filtering error: 0.012 at 32 (1.2 MB), 0.007 at 48, 0.004
// at 64 (8.4 MB)
export const TEXELS_PER_UNIT = 48;

// MeshTransmissionMaterial's noise, verbatim (whitespace aside); see
// noiseMatches()
export const NOISE_GLSL = /* glsl */ `
      vec3 random3(vec3 c) {
        float j = 4096.0*sin(dot(c,vec3(17.0, 59.4, 15.0)));
        vec3 r;
        r.z = fract(512.0*j);
        j *= .125;
        r.x = fract(512.0*j);
        j *= .125;
        r.y = fract(512.0*j);
        return r-0.5;
      }
`;
export const SIMPLEX_GLSL = /* glsl */ `
      const float F3 =  0.3333333;
      const float G3 =  0.1666667;

      float snoise(vec3 p) {
        vec3 s = floor(p + dot(p, vec3(F3)));
        vec3 x = p - s + dot(s, vec3(G3));
        vec3 e = step(vec3(0.0), x - x.yzx);
        vec3 i1 = e*(1.0 - e.zxy);
        vec3 i2 = 1.0 - e.zxy*(1.0 - e);
        vec3 x1 = x - i1 + G3;
        vec3 x2 = x - i2 + 2.0*G3;
        vec3 x3 = x - 1.0 + 3.0*G3;
        vec4 w, d;
        w.x = dot(x, x);
        w.y = dot(x1, x1);
        w.z = dot(x2, x2);
        w.w = dot(x3, x3);
        w = max(0.6 - w, 0.0);
        d.x = dot(random3(s), x);
        d.y = dot(random3(s + i1), x1);
        d.z = dot(random3(s + i2), x2);
        d.w = dot(random3(s + 1.0), x3);
        w *= w;
        w *= w;
        d *= w;
        return dot(d, vec4(52.0));
      }

      float snoiseFractal(vec3 m) {
        return 0.5333333* snoise(m)
              +0.2666667* snoise(2.0*m)
              +0.1333333* snoise(4.0*m)
              +0.0666667* snoise(8.0*m);
      }
`;

// the live wobble's line in the material's fragment shader (temporalOffset is
// zero here, but the line is replaced whole)
const LIVE_LINE = /distortionNormal = distortion \* vec3\(snoiseFractal\(vec3\(\(pos \* distortionScale \+ temporalOffset\)\)\), snoiseFractal\(vec3\(pos\.zxy \* distortionScale - temporalOffset\)\), snoiseFractal\(vec3\(pos\.yxz \* distortionScale \+ temporalOffset\)\)\);/g;

const squash = (s) => s.replace(/\s+/g, " ").trim();

// whether `fragment` (the material's shader, after its onBeforeCompile)
// still holds the noise the bake copies
export function noiseMatches(fragment) {
  const f = squash(fragment);
  return f.includes(squash(NOISE_GLSL)) && f.includes(squash(SIMPLEX_GLSL));
}

export const FIELD_PARS = /* glsl */ `
uniform highp sampler3D distortionField;
uniform mat4 distortionToField; // world position -> the field's 0..1 coordinates
`;

// The material's fragment shader with the live wobble replaced by a lookup
// in the baked field. Throws unless the line is there exactly once.
export function withBakedDistortion(fragment) {
  if (!noiseMatches(fragment)) throw new Error("baked distortion: MeshTransmissionMaterial's noise is not the one js/distortion-field.js bakes");
  const found = fragment.match(LIVE_LINE)?.length ?? 0;
  if (found !== 1) throw new Error(`baked distortion: expected one live distortion line, found ${found}`);
  return fragment.replace(LIVE_LINE,
    "distortionNormal = distortion * texture( distortionField, ( distortionToField * vec4( pos, 1.0 ) ).xyz ).rgb;");
}

// The field as a 2D atlas (bakeDistortionField's layout "atlas"), for the
// custom glass (js/glass/custom-glass.js): its depth slices side by side,
// cols to a row. Read by sampleDistortionAtlas with the field's 0..1
// coordinates (distortionToField, as for the 3D field): two bilinear lookups,
// one per neighbouring slice, mixed — the same trilinear filter, clamped at
// the edge texels' centers, as the 3D texture's. Why not the 3D texture:
// on ANGLE→Metal a sampler3D alone adds ~50 ms to a program's first
// (uncached) compile, measured on an M1 Max; a 2D sampler adds nothing.
export const FIELD_ATLAS_PARS = /* glsl */ `
uniform sampler2D distortionAtlas;
uniform vec4 distortionAtlasDims; // one slice's width and height, the slices, the columns
uniform mat4 distortionToField; // world position -> the field's 0..1 coordinates
vec3 sampleDistortionAtlas( vec3 t ) {
  vec2 slice = distortionAtlasDims.xy;
  float depth = distortionAtlasDims.z;
  float cols = distortionAtlasDims.w;
  vec2 grid = vec2( cols, ceil( depth / cols ) );
  vec2 inSlice = clamp( t.xy * slice, vec2( 0.5 ), slice - 0.5 ); // in texels
  float z = clamp( t.z * depth - 0.5, 0.0, depth - 1.0 );
  float z0 = floor( z );
  float z1 = min( z0 + 1.0, depth - 1.0 );
  vec2 at0 = vec2( mod( z0, cols ), floor( z0 / cols ) ) * slice + inSlice;
  vec2 at1 = vec2( mod( z1, cols ), floor( z1 / cols ) ) * slice + inSlice;
  vec2 size = grid * slice;
  return mix( texture( distortionAtlas, at0 / size ).rgb, texture( distortionAtlas, at1 / size ).rgb, z - z0 );
}
`;

// Bakes the field for the glass `meshes` (in `group`), at noise scale
// `scale` (the material's distortionScale). Returns { target, box }: a 3D
// render target whose texture holds the three fractals in r, g, b, and the
// group-space box it spans. Leaves the renderer's render target as it was.
// With layout "atlas", target is a 2D render target holding the slices side
// by side, and the result also has dims ([width, height, depth, columns]:
// FIELD_ATLAS_PARS's distortionAtlasDims). The atlas's program is not the 3D
// bake's, so its noise is a different draw (see the top of this file).
export function bakeDistortionField(renderer, group, meshes, scale, { layout = "3d" } = {}) {
  if (layout !== "3d" && layout !== "atlas") throw new Error(`baked distortion: layout must be "3d" or "atlas", got ${layout}`);
  group.updateWorldMatrix(true, true);
  const toGroup = group.matrixWorld.clone().invert();
  const box = new THREE.Box3();
  const meshBox = new THREE.Box3();
  for (const mesh of meshes) {
    if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
    meshBox.copy(mesh.geometry.boundingBox).applyMatrix4(new THREE.Matrix4().multiplyMatrices(toGroup, mesh.matrixWorld));
    box.union(meshBox);
  }
  if (box.isEmpty()) throw new Error("baked distortion: no glass to bake for");
  // a texel of margin: trilinear filtering clamps at the edge texels' centers
  box.expandByScalar(1.5 / TEXELS_PER_UNIT);
  const size = box.getSize(new THREE.Vector3());
  const [w, h, d] = size.toArray().map((s) => Math.ceil(s * TEXELS_PER_UNIT));

  if (layout === "atlas") return bakeAtlas(renderer, box, size, [w, h, d], scale);

  const target = new THREE.WebGL3DRenderTarget(w, h, d, { depthBuffer: false });
  // three gives a 3D target a texture of its own, ignoring the texture
  // options: set them on it (the default is 8-bit unsigned, nearest)
  Object.assign(target.texture, {
    type: THREE.HalfFloatType,
    format: THREE.RGBAFormat,
    minFilter: THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
    generateMipmaps: false,
  });
  // each fragment evaluates the noise at its texel's center, so that a
  // lookup at a center returns the live value there exactly
  const material = new THREE.ShaderMaterial({
    uniforms: {
      boxMin: { value: box.min },
      boxSize: { value: size },
      dims: { value: new THREE.Vector3(w, h, d) },
      layer: { value: 0 },
      distortionScale: { value: scale },
    },
    vertexShader: /* glsl */ `
      void main() { gl_Position = vec4( position.xy, 0.0, 1.0 ); }
    `,
    fragmentShader: NOISE_GLSL + SIMPLEX_GLSL + /* glsl */ `
      uniform vec3 boxMin;
      uniform vec3 boxSize;
      uniform vec3 dims;
      uniform float layer;
      uniform float distortionScale;
      void main() {
        vec3 pos = boxMin + vec3( gl_FragCoord.xy, layer + 0.5 ) / dims * boxSize;
        gl_FragColor = vec4( snoiseFractal( pos * distortionScale ), snoiseFractal( pos.zxy * distortionScale ),
          snoiseFractal( pos.yxz * distortionScale ), 1.0 );
      }
    `,
    depthTest: false,
    depthWrite: false,
  });
  const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
  quad.frustumCulled = false;
  const camera = new THREE.Camera();
  const previous = renderer.getRenderTarget();
  try {
    for (let z = 0; z < d; z++) {
      material.uniforms.layer.value = z;
      renderer.setRenderTarget(target, z);
      renderer.render(quad, camera);
    }
  } finally {
    renderer.setRenderTarget(previous);
    quad.geometry.dispose();
    material.dispose();
  }
  return { target, box };
}

// the atlas layout's bake: the 3D bake's slices, each drawn into its own
// tile of one 2D target (its viewport), the fragment's texel taken relative
// to the tile
function bakeAtlas(renderer, box, size, [w, h, d], scale) {
  const cols = Math.ceil(Math.sqrt(d * h / w)); // a roughly square atlas
  const rows = Math.ceil(d / cols);
  const target = new THREE.WebGLRenderTarget(w * cols, h * rows, {
    type: THREE.HalfFloatType,
    minFilter: THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
    generateMipmaps: false,
    depthBuffer: false,
  });
  const material = new THREE.ShaderMaterial({
    uniforms: {
      boxMin: { value: box.min },
      boxSize: { value: size },
      dims: { value: new THREE.Vector3(w, h, d) },
      layer: { value: 0 },
      tileOrigin: { value: new THREE.Vector2() },
      distortionScale: { value: scale },
    },
    vertexShader: /* glsl */ `
      void main() { gl_Position = vec4( position.xy, 0.0, 1.0 ); }
    `,
    fragmentShader: NOISE_GLSL + SIMPLEX_GLSL + /* glsl */ `
      uniform vec3 boxMin;
      uniform vec3 boxSize;
      uniform vec3 dims;
      uniform float layer;
      uniform vec2 tileOrigin;
      uniform float distortionScale;
      void main() {
        vec3 pos = boxMin + vec3( gl_FragCoord.xy - tileOrigin, layer + 0.5 ) / dims * boxSize;
        gl_FragColor = vec4( snoiseFractal( pos * distortionScale ), snoiseFractal( pos.zxy * distortionScale ),
          snoiseFractal( pos.yxz * distortionScale ), 1.0 );
      }
    `,
    depthTest: false,
    depthWrite: false,
  });
  const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
  quad.frustumCulled = false;
  const camera = new THREE.Camera();
  const previous = renderer.getRenderTarget();
  try {
    for (let z = 0; z < d; z++) {
      const x = (z % cols) * w, y = Math.floor(z / cols) * h;
      material.uniforms.layer.value = z;
      material.uniforms.tileOrigin.value.set(x, y);
      // the renderer clears before drawing: the scissor keeps it to the tile
      target.viewport.set(x, y, w, h);
      target.scissor.set(x, y, w, h);
      target.scissorTest = true;
      renderer.setRenderTarget(target);
      renderer.render(quad, camera);
    }
  } finally {
    target.viewport.set(0, 0, target.width, target.height);
    target.scissor.set(0, 0, target.width, target.height);
    target.scissorTest = false;
    renderer.setRenderTarget(previous);
    quad.geometry.dispose();
    material.dispose();
  }
  return { target, box, dims: [w, h, d, cols] };
}

// world -> the field's 0..1 coordinates, for the group's current pose (its
// matrixWorld up to date): into the group's space, then the box onto the
// unit cube. Writes `out`; allocates nothing (called every frame).
const toBox = new THREE.Matrix4();
const toGroup = new THREE.Matrix4();
export function fieldMatrix(out, group, box) {
  toBox.makeScale(1 / (box.max.x - box.min.x), 1 / (box.max.y - box.min.y), 1 / (box.max.z - box.min.z))
    .multiply(toGroup.makeTranslation(-box.min.x, -box.min.y, -box.min.z));
  return out.multiplyMatrices(toBox, toGroup.copy(group.matrixWorld).invert());
}
